'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [currentYear, setCurrentYear] = useState<number>(2026);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
  }, []);

  const footerLinks = {
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Solutions', href: '/solutions' },
      { label: 'Contact', href: '/contact' }
    ],
    solutions: [
      { label: 'Banking Uniforms', href: '/solutions#banking' },
      { label: 'Aviation Apparel', href: '/solutions#aviation' },
      { label: 'Government Sector', href: '/solutions#government' },
      { label: 'Corporate Wear', href: '/solutions#corporate' }
    ],
    contact: [
      { icon: 'MapPinIcon', text: 'Dar es Salaam, Tanzania' },
      { icon: 'PhoneIcon', text: '+255 22 123 4567' },
      { icon: 'EnvelopeIcon', text: 'info@pinkdiamond.co.tz' }
    ]
  };

  return (
    <footer className={`bg-primary text-white ${className}`}>
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center space-x-2 mb-6">
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-white"
              >
                <path
                  d="M20 2L4 12L20 22L36 12L20 2Z"
                  fill="currentColor"
                  opacity="0.9"
                />
                <path
                  d="M20 22L4 12V28L20 38L36 28V12L20 22Z"
                  fill="var(--color-accent)"
                  opacity="0.7"
                />
                <circle
                  cx="20"
                  cy="20"
                  r="3"
                  fill="var(--color-brand-gold)"
                />
              </svg>
              <span className="text-xl font-headline font-semibold">
                Pink Diamond
              </span>
            </div>
            <p className="text-sm font-body opacity-80 leading-relaxed">
              Tanzania's premier corporate apparel provider, trusted by leading institutions for over two decades.
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-headline font-semibold mb-4">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm font-body opacity-80 hover:opacity-100 transition-opacity"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-headline font-semibold mb-4">Solutions</h3>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm font-body opacity-80 hover:opacity-100 transition-opacity"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-headline font-semibold mb-4">Contact</h3>
            <ul className="space-y-3">
              {footerLinks.contact.map((item, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Icon
                    name={item.icon as any}
                    size={20}
                    variant="outline"
                    className="opacity-80 flex-shrink-0 mt-0.5"
                  />
                  <span className="text-sm font-body opacity-80">
                    {item.text}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm font-body opacity-80">
              &copy; {currentYear} Pink Diamond Company Limited. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link href="/privacy" className="text-sm font-body opacity-80 hover:opacity-100 transition-opacity">
                Privacy Policy
              </Link>
              <Link href="/terms" className="text-sm font-body opacity-80 hover:opacity-100 transition-opacity">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;